import React, { useState } from 'react';
import DocumentsPanel from '../documents/DocumentsPanel';
import AccountPaymentsPanel from '../payments/AccountPaymentsPanel';
import { supabaseClient } from '../../config/supabase';
import AffiliateTripLinkPanel from './AffiliateTripLinkPanel';
import { ASSIGNMENT_LIFECYCLE, ASSIGNMENT_SIDE_EXITS, assignmentStatusMeta, isSideExit } from './dispatchConstants';

function truncatedId(id) {
    return id ? `${id.slice(0, 8)}…` : '—';
}

function driverPrimaryLabel(driver, fallbackId = null) {
    const profileName = driver?.user_profile?.full_name?.trim();
    return profileName || truncatedId(driver?.id || fallbackId);
}

// Inline assignment detail card — same "cascade under the row" grammar as EngagementDetailCard.
// Surfaces the linked engagement's confirmation_number as a visible reference back to Module 0
// (BL-183 requirement, locked in BL-168 v1.01).
function AssignmentDetailCard({ assignment, drivers = [], vehicles = [], onClose, onStatusChange, theme = 'light', userId }) {
    const [documentsOpen, setDocumentsOpen] = useState(false);
    const [paymentsOpen, setPaymentsOpen] = useState(false);
    const dark = theme === 'dark';
    const meta = assignmentStatusMeta(assignment.status);

    const assignedDriverId = assignment.driver_id || assignment.affiliate_id;
    const driver = drivers.find(d => d.id === assignedDriverId);
    const vehicle = vehicles.find(v => v.id === assignment.vehicle_id);

    const cardCls = dark ? 'bg-gray-800 border-gray-600 text-gray-100' : 'bg-white border-gray-200 text-gray-800';
    const labelCls = dark ? 'text-gray-400' : 'text-gray-500';
    const valueCls = dark ? 'text-gray-100' : 'text-gray-800';
    const dividerCls = dark ? 'border-gray-700' : 'border-gray-100';

    // Steps visible on the lifecycle bar (side-exit statuses live below as badges, not on the bar).
    const steps = ASSIGNMENT_LIFECYCLE;
    const currentIdx = steps.indexOf(assignment.status);

    const StatusPill = ({ status }) => {
        const m = assignmentStatusMeta(status);
        return (
            <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${m.pill}`}>
                {m.icon} {m.label}
            </span>
        );
    };

    return (
        <div className={`ia-dispatch rounded-xl border p-5 mt-1 ${cardCls}`}>
            {/* Close + header */}
            <div className="flex items-start justify-between mb-4">
                <div>
                    <h3 className="text-base font-semibold">Assignment Detail</h3>
                    {/* BL-183: confirmation_number back-reference to Module 0 */}
                    <p className={`text-xs mt-0.5 ${labelCls}`}>
                        Engagement:{' '}
                        <span className="font-mono font-semibold text-[var(--accent-ink)]">
                            {assignment.confirmation_number || '—'}
                        </span>
                        {' '}(Attempt #{assignment.attempt_sequence})
                    </p>
                </div>
                <button
                    onClick={onClose}
                    className={`text-lg leading-none p-1 rounded hover:bg-gray-200/40 ${labelCls}`}
                    aria-label="Close detail"
                >
                    ✕
                </button>
            </div>

            {/* Lifecycle bar */}
            {!isSideExit(assignment.status) && (
                <div className="flex items-center gap-1 mb-5 overflow-x-auto pb-1">
                    {steps.map((step, idx) => {
                        const m = assignmentStatusMeta(step);
                        const done = idx < currentIdx;
                        const current = idx === currentIdx;
                        return (
                            <React.Fragment key={step}>
                                <div className={`flex flex-col items-center min-w-[72px]`}>
                                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-sm
                                        ${current ? 'ring-2 ring-[var(--accent-ink)] bg-[var(--btn-fill)] text-[var(--btn-ink)]'
                                            : done ? 'bg-green-500 text-white'
                                            : dark ? 'bg-gray-600 text-gray-400' : 'bg-gray-200 text-gray-400'}`}>
                                        {done ? '✓' : m.icon}
                                    </div>
                                    <span className={`text-[10px] mt-1 text-center leading-tight ${current ? 'font-semibold' : labelCls}`}>{m.label}</span>
                                </div>
                                {idx < steps.length - 1 && (
                                    <div className={`flex-1 h-0.5 min-w-[12px] ${done ? 'bg-green-400' : dark ? 'bg-gray-600' : 'bg-gray-200'}`} />
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>
            )}

            {/* Side-exit pill when applicable */}
            {isSideExit(assignment.status) && (
                <div className="mb-4">
                    <StatusPill status={assignment.status} />
                </div>
            )}

            <nav className="m9-document-actions mb-4" aria-label="Trip sections"><button className="m9-documents-pill" style={{ minHeight: 44, padding: '.6rem 1rem' }} aria-pressed={!documentsOpen && !paymentsOpen} onClick={() => { setDocumentsOpen(false); setPaymentsOpen(false); }}>Details</button><button className="m9-documents-pill" style={{ minHeight: 44, padding: '.6rem 1rem' }} aria-pressed={documentsOpen} onClick={() => { setDocumentsOpen(true); setPaymentsOpen(false); }}>Documents</button><button className="m9-documents-pill" style={{ minHeight: 44, padding: '.6rem 1rem' }} aria-pressed={paymentsOpen} onClick={() => { setPaymentsOpen(true); setDocumentsOpen(false); }}>Payments</button></nav>
            {documentsOpen && <DocumentsPanel client={supabaseClient} tripId={assignment.engagement_id} staff canEdit={Boolean(onStatusChange)} />}
            {paymentsOpen && <AccountPaymentsPanel client={supabaseClient} tripId={assignment.engagement_id} />}
            <div hidden={documentsOpen || paymentsOpen}>
            {/* Detail grid */}
            <div className={`grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3 text-sm border-t pt-4 ${dividerCls}`}>
                <div>
                    <p className={`text-xs uppercase tracking-wide ${labelCls}`}>Current Status</p>
                    <div className="mt-1"><StatusPill status={assignment.status} /></div>
                </div>
                <div>
                    <p className={`text-xs uppercase tracking-wide ${labelCls}`}>Type</p>
                    <p className={`mt-1 capitalize ${valueCls}`}>{assignment.assignment_type || '—'}</p>
                </div>
                <div>
                    <p className={`text-xs uppercase tracking-wide ${labelCls}`}>Attempt #</p>
                    <p className={`mt-1 font-mono ${valueCls}`}>{assignment.attempt_sequence ?? '—'}</p>
                </div>
                <div>
                    <p className={`text-xs uppercase tracking-wide ${labelCls}`}>Driver</p>
                    <p className={`mt-1 ${valueCls}`}>
                        {assignedDriverId
                            ? driverPrimaryLabel(driver, assignedDriverId)
                            : '—'}
                    </p>
                </div>
                <div>
                    <p className={`text-xs uppercase tracking-wide ${labelCls}`}>Vehicle</p>
                    <p className={`mt-1 ${valueCls}`}>
                        {vehicle
                            ? `${vehicle.label || vehicle.vehicle_class || '—'} ${vehicle.plate ? `(${vehicle.plate})` : ''}`.trim()
                            : (assignment.vehicle_id ? <span className="italic text-gray-400">ID {assignment.vehicle_id.slice(0, 8)}…</span> : '—')}
                    </p>
                </div>
                {assignment.assignment_type === 'affiliate' && (
                    <div>
                        <p className={`text-xs uppercase tracking-wide ${labelCls}`}>Ext. Reference</p>
                        <p className={`mt-1 font-mono ${valueCls}`}>{assignment.external_reference || '—'}</p>
                    </div>
                )}
                <div>
                    <p className={`text-xs uppercase tracking-wide ${labelCls}`}>Offer Timeout</p>
                    <p className={`mt-1 ${valueCls}`}>{assignment.offer_timeout_minutes != null ? `${assignment.offer_timeout_minutes} min` : '—'}</p>
                </div>
                <div>
                    <p className={`text-xs uppercase tracking-wide ${labelCls}`}>Created</p>
                    <p className={`mt-1 ${valueCls}`}>{assignment.created_at ? new Date(assignment.created_at).toLocaleString() : '—'}</p>
                </div>
                <div>
                    <p className={`text-xs uppercase tracking-wide ${labelCls}`}>Last Updated</p>
                    <p className={`mt-1 ${valueCls}`}>{assignment.updated_at ? new Date(assignment.updated_at).toLocaleString() : '—'}</p>
                </div>
            </div>

            {assignment.assignment_type === 'affiliate' && (
                <AffiliateTripLinkPanel assignment={assignment} theme={theme} userId={userId} />
            )}

            {/* Status transition actions — Dispatch-tier only (Gate 1) */}
            {onStatusChange && !isTerminal(assignment.status) && (
                <div className={`mt-4 pt-4 border-t ${dividerCls}`}>
                    <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${labelCls}`}>Update Status</p>
                    <div className="flex flex-wrap gap-2">
                        {ALLOWED_TRANSITIONS[assignment.status]?.map(next => {
                            const m = assignmentStatusMeta(next);
                            return (
                                <button
                                    key={next}
                                    onClick={() => onStatusChange(assignment.id, next)}
                                    className="px-3 py-1.5 text-xs font-medium rounded-lg bg-[var(--btn-fill)] text-[var(--btn-ink)] hover:bg-[var(--btn-fill-hover)]"
                                >
                                    {m.icon} {m.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
            </div>
        </div>
    );
}

// Dispatcher-allowed transitions (app-layer; DB enforces via trigger).
const ALLOWED_TRANSITIONS = {
    offered:           ['accepted', 'declined', 'timed_out'],
    accepted:          ['en_route', 'reassigned'],
    en_route:          ['on_location', 'reassigned'],
    on_location:       ['passenger_onboard', 'reassigned'],
    passenger_onboard: ['completed', 'reassigned'],
    completed:         [],
    declined:          [],
    timed_out:         [],
    reassigned:        [],
};

function isTerminal(status) {
    return ['completed', 'declined', 'timed_out'].includes(status);
}

export default AssignmentDetailCard;
